import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ── Provider-type enum ──────────────────────────────────────────────
export const IdpProviderTypeSchema = z.enum(['SAML', 'OIDC']);
export type IdpProviderType = z.infer<typeof IdpProviderTypeSchema>;

// ── SAML provider details (base object for partial usage) ───────────
export const SamlProviderDetailsBaseSchema = z.object({
  MetadataURL: z.string().url('Must be a valid URL').optional(),
  MetadataFile: z.string().min(1).optional(),
  IDPSignout: z.string().url('Must be a valid URL').optional(),
  IDPInit: z.enum(['true', 'false']).optional(),
  RequestSigningAlgorithm: z.enum(['rsa-sha256', 'rsa-sha1']).optional(),
  EncryptedResponses: z.enum(['true', 'false']).optional(),
});

// Create variant adds the MetadataURL-or-MetadataFile refinement
export const SamlProviderDetailsSchema = SamlProviderDetailsBaseSchema.refine(
  (d) => d.MetadataURL || d.MetadataFile,
  { message: 'Either MetadataURL or MetadataFile must be provided', path: ['MetadataURL'] },
);
export type SamlProviderDetails = z.infer<typeof SamlProviderDetailsSchema>;

// ── OIDC provider details ───────────────────────────────────────────
export const OidcProviderDetailsSchema = z.object({
  client_id: z.string().min(1, 'client_id is required'),
  client_secret: z.string().min(1, 'client_secret is required'),
  oidc_issuer: z.string().url('Must be a valid issuer URL'),
  authorize_scopes: z.string().min(1, 'authorize_scopes is required'),
  authorize_url: z.string().url('Must be a valid URL').optional(),
  token_url: z.string().url('Must be a valid URL').optional(),
  attributes_url: z.string().url('Must be a valid URL').optional(),
  jwks_uri: z.string().url('Must be a valid URL').optional(),
  attributes_request_method: z.enum(['GET', 'POST']).optional(),
});
export type OidcProviderDetails = z.infer<typeof OidcProviderDetailsSchema>;

// ── Attribute mapping ───────────────────────────────────────────────
export const IdpAttributeMappingSchema = z.record(z.string(), z.string()).optional();

// ── Create SAML IdP request ─────────────────────────────────────────
export const CreateSamlIdpRequestSchema = z.object({
  tenant_id: uuidSchema,
  provider_name: z
    .string()
    .min(1, 'Provider name is required')
    .max(128, 'Provider name must be at most 128 characters')
    .regex(/^[\w\s._/-]+$/, 'Provider name contains invalid characters'),
  provider_type: z.literal('SAML'),
  provider_details: SamlProviderDetailsSchema,
  attribute_mapping: IdpAttributeMappingSchema,
  idp_identifiers: z.array(z.string().min(1).max(40)).max(50).optional(),
});
export type CreateSamlIdpRequest = z.infer<typeof CreateSamlIdpRequestSchema>;

// ── Create OIDC IdP request ─────────────────────────────────────────
export const CreateOidcIdpRequestSchema = z.object({
  tenant_id: uuidSchema,
  provider_name: z
    .string()
    .min(1, 'Provider name is required')
    .max(128, 'Provider name must be at most 128 characters')
    .regex(/^[\w\s._/-]+$/, 'Provider name contains invalid characters'),
  provider_type: z.literal('OIDC'),
  provider_details: OidcProviderDetailsSchema,
  attribute_mapping: IdpAttributeMappingSchema,
  idp_identifiers: z.array(z.string().min(1).max(40)).max(50).optional(),
});
export type CreateOidcIdpRequest = z.infer<typeof CreateOidcIdpRequestSchema>;

// ── Discriminated union for create ──────────────────────────────────
export const CreateIdpRequestSchema = z.discriminatedUnion('provider_type', [
  CreateSamlIdpRequestSchema,
  CreateOidcIdpRequestSchema,
]);
export type CreateIdpRequest = z.infer<typeof CreateIdpRequestSchema>;

// ── Update SAML IdP request ─────────────────────────────────────────
export const UpdateSamlIdpRequestSchema = z.object({
  tenant_id: uuidSchema,
  provider_type: z.literal('SAML'),
  provider_details: SamlProviderDetailsBaseSchema.partial().optional(),
  attribute_mapping: IdpAttributeMappingSchema,
  idp_identifiers: z.array(z.string().min(1).max(40)).max(50).optional(),
});
export type UpdateSamlIdpRequest = z.infer<typeof UpdateSamlIdpRequestSchema>;

// ── Update OIDC IdP request ─────────────────────────────────────────
export const UpdateOidcIdpRequestSchema = z.object({
  tenant_id: uuidSchema,
  provider_type: z.literal('OIDC'),
  provider_details: OidcProviderDetailsSchema.partial().optional(),
  attribute_mapping: IdpAttributeMappingSchema,
  idp_identifiers: z.array(z.string().min(1).max(40)).max(50).optional(),
});
export type UpdateOidcIdpRequest = z.infer<typeof UpdateOidcIdpRequestSchema>;

// ── Discriminated union for update ──────────────────────────────────
export const UpdateIdpRequestSchema = z.discriminatedUnion('provider_type', [
  UpdateSamlIdpRequestSchema,
  UpdateOidcIdpRequestSchema,
]);
export type UpdateIdpRequest = z.infer<typeof UpdateIdpRequestSchema>;

// ── IdP response ────────────────────────────────────────────────────
export const IdpResponseSchema = z.object({
  provider_name: z.string(),
  provider_type: IdpProviderTypeSchema,
  provider_details: z.record(z.string(), z.string()),
  attribute_mapping: z.record(z.string(), z.string()).optional(),
  idp_identifiers: z.array(z.string()).optional(),
  creation_date: z.string(),
  last_modified_date: z.string(),
});
export type IdpResponse = z.infer<typeof IdpResponseSchema>;

// ── List IdPs response ──────────────────────────────────────────────
export const ListIdpsResponseSchema = z.object({
  providers: z.array(IdpResponseSchema),
});
export type ListIdpsResponse = z.infer<typeof ListIdpsResponseSchema>;

// ── Clone IdP to another pool ───────────────────────────────────────
export const CloneIdpRequestSchema = z.object({
  source_tenant_id: uuidSchema,
  target_tenant_id: uuidSchema,
  attribute_mapping: IdpAttributeMappingSchema,
  idp_identifiers: z.array(z.string().min(1).max(40)).max(50).optional(),
});
export type CloneIdpRequest = z.infer<typeof CloneIdpRequestSchema>;

// ── Cross-pool scan — entry per unique provider ─────────────────────
export const CrossPoolIdpSchema = z.object({
  provider_name: z.string(),
  provider_type: IdpProviderTypeSchema,
  exists_in_pools: z.array(
    z.object({ tenant_id: z.string(), user_pool_id: z.string() }),
  ),
});
export type CrossPoolIdp = z.infer<typeof CrossPoolIdpSchema>;

export const AllIdpsResponseSchema = z.object({
  providers: z.array(CrossPoolIdpSchema),
  total_pools_scanned: z.number(),
});
export type AllIdpsResponse = z.infer<typeof AllIdpsResponseSchema>;

// ── IAM OIDC providers ───────────────────────────────────────────────
export const IamOidcProviderSchema = z.object({
  arn: z.string(),
  url: z.string().url('Must be a valid URL'),
  client_ids: z.array(z.string()),
  thumbprints: z.array(z.string()),
  create_date: z.string().optional(),
});
export type IamOidcProvider = z.infer<typeof IamOidcProviderSchema>;

export const IamOidcProvidersResponseSchema = z.object({
  providers: z.array(IamOidcProviderSchema),
});
export type IamOidcProvidersResponse = z.infer<typeof IamOidcProvidersResponseSchema>;

// ── Unified discovery response ───────────────────────────────────────
export const UnifiedIdpDiscoveryResponseSchema = z.object({
  cognito_idps: AllIdpsResponseSchema,
  iam_oidc_providers: IamOidcProvidersResponseSchema,
  summary: z.object({
    total_pools_scanned: z.number(),
    total_cognito_idps: z.number(),
    unique_cognito_idps: z.number(),
    total_iam_oidc_providers: z.number(),
  }),
});
export type UnifiedIdpDiscoveryResponse = z.infer<typeof UnifiedIdpDiscoveryResponseSchema>;

// ── Params schemas ──────────────────────────────────────────────────
export const IdpProviderNameParamSchema = z.object({
  provider_name: z.string().min(1),
});
